/**
 * <contact-form>: sends the form without leaving the page. Without JavaScript the form posts normally and the
 * server answers with the "sent" page or the form again with the problem marked.
 */

// Dependencies
import { registerComponent } from 'almostnojs';
import { Enhanced } from './enhanced.js';

/**
 * The contact form.
 */
class ContactForm extends Enhanced {

    /**
     * Take over submission.
     *
     * @returns {void}
     */
    init() {

        // Parts.
        this.form = this.querySelector('form');
        this.status = this.querySelector('.form-status');
        this.button = this.querySelector('button[type="submit"]');
        if (!this.form || !this.status || !this.button) return;

        // Submit.
        this.listen(this.form, 'submit', (e) => { e.preventDefault(); this.send(); });
    }

    /**
     * Show a status line.
     *
     * @param {string} text - Message.
     * @param {string} [kind] - 'error' or 'ok'.
     * @returns {void}
     */
    say(text, kind = '') {

        // Text and tone.
        this.status.textContent = text;
        this.status.className = `form-status${kind ? ` ${kind}` : ''}`;
    }

    /**
     * Mark fields the browser considers invalid.
     *
     * @returns {HTMLElement|null} The first invalid field.
     */
    check() {

        // Each real field.
        let first = null;
        this.form.querySelectorAll('input:not([type="hidden"]), select, textarea').forEach((field) => {
            if (field.name === 'website') return;
            const ok = field.checkValidity();
            if (ok) field.removeAttribute('aria-invalid');
            else { field.setAttribute('aria-invalid', 'true'); first ??= field; }
        });

        // First problem.
        return first;
    }

    /**
     * Validate and send.
     *
     * @returns {Promise<void>}
     */
    async send() {

        // Local checks first.
        const bad = this.check();
        if (bad) {
            this.say(bad.id === 'message' ? 'Please write at least 20 characters.' : 'Please complete the highlighted fields.', 'error');
            bad.focus();
            return;
        }

        // Sending.
        this.button.disabled = true;
        this.say('Sending…');

        // Post as JSON. fetch rather than $.post: the server explains a refusal in the body of a 4xx/5xx reply,
        // and $.post discards the body of any non-2xx response.
        const body = JSON.stringify(Object.fromEntries(new FormData(this.form)));
        try {
            const response = await fetch(this.form.action, { method: 'POST', body, headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, signal: AbortSignal.timeout(20000) });
            const reply = await response.json().catch(() => null);
            if (response.ok && reply?.ok) { window.location.assign('/contact/sent'); return; }
            if (reply?.token) this.form.querySelector('input[name="token"]')?.setAttribute('value', reply.token);
            this.fail(reply?.error, reply?.field);
        } catch {
            this.fail('Network error. Please try again, or email contact@olevicor.com.');
        } finally {
            this.button.disabled = false;
        }
    }

    /**
     * Report a failure.
     *
     * @param {string|null} error - Message.
     * @param {string|null} [field] - Field to mark.
     * @returns {void}
     */
    fail(error, field = null) {

        // Turnstile tokens are single-use.
        window.turnstile?.reset?.();

        // Mark the field the server named.
        if (field) this.form.querySelector(`[name="${field}"]`)?.setAttribute('aria-invalid', 'true');

        // Say why.
        this.say(error || 'Your message could not be sent. Please try again, or email contact@olevicor.com.', 'error');
    }
}

// Register
registerComponent('contact-form', ContactForm);
